/* eslint-disable max-statements */
import { Subject, takeUntil } from 'rxjs';
import { IchatRoom, IchatRoomAndLastMsg, IpeerInfo } from '../../shared/core-types/chat';
import { ECHATMETHOD } from '../../shared/core-types/chat.enum';
import { TchatMsgStatus } from '../../shared/core-types/union.types';
import { makeRandomString } from './constants/makerandomstring.constant';
import { ChatMsg, ChatRoom } from './utils/chat-room';
import { ChatSocket } from './utils/chat-socket';
import { Logger } from './utils/logger';


/**
 * Local representation of a room paired with its last message.
 */
export interface IChatRoomAndLastMsg {
  room: ChatRoom;
  lastMsg: ChatMsg | null;
}

/**
 * Represents a controller for the WireTalk application.
 *
 * @remarks
 * - Orchestrates socket communication, room management, and message state.
 * - Emits requests via {@link ChatSocket} and reacts to notifications through subscriptions.
 * - Maintains a local cache of rooms and messages for the active session.
 *
 * @example
 * const client = new WireTalkClient(baseUrl, getToken, myId, myName, myPhoto, 'chat-root');
 * // create or select a room, then:
 * await client.joinMainRoom();
 */
export class WireTalkClient {
  /** All messages in the currently active room */
  messages: ChatMsg[] = [];

  /** Target peer for directed messages; defaults to 'all' */
  toPeer = 'all';

  /** Currently active room context */
  activeRoom: ChatRoom | null = null;

  /** All joined rooms with their last message */
  allRooms: IChatRoomAndLastMsg[] = [];

  /** Teardown notifier for rxjs subscriptions */
  destroyed$ = new Subject();

  /** Internal logger */
  logger = new Logger();

  /** Socket abstraction for server communication */
  chatSocket: ChatSocket;

  /** Aggregate count of unviewed messages across rooms */
  unviewedMsgsLength = 0;

  /**
   * Creates an instance of the WireTalk client controller.
   *
   * @param url - Base URL for the chat socket connection.
   * @param getAuthToken - Async function returning an auth token used by the socket.
   * @param myId - The current user's unique identifier.
   * @param myNames - Display name for the current user.
   * @param myPhotoUrl - URL of the current user's avatar/photo.
   * @param chatElementId - ID of the chat container element used for scrolling.
   */
  constructor(
    private url: string,
    private getAuthToken: () => Promise<string>,
    private myId: string,
    private myNames: string,
    private myPhotoUrl: string,
    chatElementId: string
  ) {
    this.chatSocket = new ChatSocket(this.url, myId, this.getAuthToken);
    this.runSubscriptions(chatElementId);
  }

  /**
   * Total count of unviewed messages across all rooms.
   */
  get unviewedMessagesCount() {
    return this.allRooms.reduce((acc, room) => acc + (room.room.unviewedMsgsLength || 0), 0);
  }

  /* recalculateUnviewedMsgsLength() {
    this.unviewedMsgsLength = this.allRooms.reduce((acc, room) => acc + (room.room.unviewedMsgsLength || 0), 0);
  } */

  /**
   * Filters rooms by searching for a peer's name (excluding the current user).
   *
   * @param search - Case-insensitive search term for peer names.
   * @returns Subset of rooms whose non-self peer matches the search.
   */
  filterRoomsByPeer(search: string) {
    return this.allRooms
      .filter(value => {
        const found = value.room.peers.find(peer => peer.id !== this.myId);

        if (found) {
          return found.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
        }

        return false;
      });
  }

  /**
   * Determines the local peer information.
   * @returns The local peer information.
   */
  determineLocalPeerInfo() {
    const peerInfo: IpeerInfo = {
      id: this.myId,
      name: this.myNames,
      photo: this.myPhotoUrl,
      roomAdmin: false,
      lastSeen: new Date(),
      online: true,
      unviewedMsgsLength: 0
    };

    return peerInfo;
  }

  /**
   * Joins the active room and adds new peers to the chat.
   * @returns A promise that resolves when the room is joined.
   */
  async joinRoom() {
    if (!this.activeRoom) {
      return;
    }
    const { joined, peers } = await this.join(this.determineLocalPeerInfo());

    if (joined) {
      return;
    }

    this.logger.debug('joined, peersinfo: %s', JSON.stringify(peers));

    for (const peer of peers) {
      this.newPeer(peer);
    }
  }

  /**
   * Joins the main chat room.
   * @returns A promise that resolves when the main room is joined.
   */
  async joinMainRoom() {
    const { joined, onlineRoomPeers, chatRoomAndLastMsg, totalUnviewedLength } = await this.joinMain(this.determineLocalPeerInfo());

    this.allRooms = chatRoomAndLastMsg.map(room => ({ room: new ChatRoom(room.room), lastMsg: new ChatMsg(this.myId, room.lastMsg) }));

    this.unviewedMsgsLength = totalUnviewedLength;

    this.logger.debug('joined main room, unviewedMsgsLength: %s', this.unviewedMsgsLength);

    if (joined) {
      return;
    }

    this.logger.debug('joined main room, peersinfo: %s', JSON.stringify(onlineRoomPeers));

    // rejoin if active room
    if (this.activeRoom) {
      this.reconnectToRoom();
    }
  }

  /**
   * Joins the main chat room via a server request.
   *
   * @param params - Payload describing the local user to announce (e.g., {@link IpeerInfo}).
   * @returns An object including current online peers, last message per room, a joined flag, and total unviewed count.
   */
  async joinMain(params) {
    const callRes = await this.chatSocket.sendOnlineSoloRequest(
      ECHATMETHOD.JOIN,
      params
    );

    this.logger.debug('joinMain, res: %s', JSON.stringify(callRes));

    return callRes as { onlineRoomPeers: IpeerInfo[]; joined: boolean; chatRoomAndLastMsg: IchatRoomAndLastMsg<IpeerInfo>[]; totalUnviewedLength: number };
  }

  /**
   * Joins the currently active room with the specified parameters.
   *
   * @param params - Parameters for joining the room (e.g., peer info).
   * @returns Resolves with `{ peers, joined }` where `peers` are the current room members.
   * @throws If no active room is selected.
   */
  async join(params) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }
    const callRes = await this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.JOIN,
      params
    );

    return callRes as { peers: IpeerInfo[]; joined: boolean };
  }

  /**
   * Sends a chat message.
   *
   * @param chatMessage - The message text to send.
   * @param chatElementId - The DOM element id used for autoscroll after queuing the message.
   * @returns A promise that resolves when the message is acknowledged by the server; updates local state on success/failure.
   * @throws If there is no active room.
   */
  send(chatMessage: string, chatElementId: string) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }
    const id = this.activeRoom.id + makeRandomString(22, 'combined');
    const createTime = new Date();
    const msg = new ChatMsg(
      this.myId,
      {
        id,
        peerInfo: this.determineLocalPeerInfo(),
        roomId: this.activeRoom.id,
        msg: chatMessage,
        createTime,
        who: 'me',
        status: 'pending',
        deleted: false
      }
    );

    this.messages = [...this.messages, msg];
    this.scrollToLast(chatElementId);

    return this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.CHAT_MESSAGE,
      {
        id,
        roomId: this.activeRoom.id,
        chatMessage,
        createTime,
        from: this.myId,
        to: this.toPeer
      }
    ).then(() => {
      msg.status = 'sent';
      const found = this.allRooms.find(room => room.room.id === this.activeRoom?.id);

      if (found && found.lastMsg) {
        if (found.lastMsg.createTime < msg.createTime) {
          found.lastMsg = msg;
        }
      }
    }).catch(err => {
      this.logger.error('send MESSAGE ERROR', err);
      msg.status = 'failed';
    });
  }

  /**
   * Updates the status of one or more chat messages (e.g., 'viewed').
   *
   * @param status - The new status to apply.
   * @param msgIds - IDs of messages to update.
   * @returns A promise that resolves with the server response; triggers count refresh on success.
   * @throws If there is no active room.
   */
  async updateStatus(status: TchatMsgStatus, msgIds: string[]) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }

    const updated = await this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.UPDATE_STATUS,
      {
        ids: msgIds,
        status,
        statusField: status,
        from: this.myId,
        to: this.toPeer
      }
    );

    this.logger.debug('updateStatus, updated: %s', updated);

    if (updated) {
      if (this.activeRoom?.id && this.myId) {
        this.logger.debug('getUnviewedMsgsLength: %s', this.myId);
        this.getUnviewedMsgsLength(this.myId, this.activeRoom.id);
      }
    }

    return updated;
  }

  /**
   * Deletes or restores a message.
   *
   * @param id - The message id.
   * @param deleted - `true` to mark as deleted, `false` to restore.
   * @returns A promise resolving to `true` on success.
   * @throws If there is no active room.
   */
  deleteRestoreMessage(id: string, deleted: boolean) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }

    return this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.DELETE_MESSAGE,
      {
        id,
        deleted,
        from: this.myId,
        to: this.toPeer
      }
    ).then(() => true).catch(err => err);
  }

  /**
   * Requests to close a peer's session in the active room.
   *
   * @param stopClass - Whether to stop the class/session for the peer.
   * @returns A promise that resolves when the request is sent.
   * @throws If there is no active room.
   */
  sendClosePeer(stopClass: boolean) {
    this.logger.debug('sendClosePeer');

    if (!this.activeRoom) {
      throw new Error('no active room');
    }

    return this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.CLOSE_PEER,
      {
        stopClass
      }
    );
  }

  /**
   * Updates the peer information in the active room.
   *
   * @param peerInfo - The updated peer information.
   * @returns A promise that resolves when the request is sent.
   * @throws If there is no active room.
   */
  updatePeer(peerInfo: IpeerInfo) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }

    return this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.PEER_UPDATE,
      {
        peerInfo,
        from: this.myId,
        to: 'all'
      }
    );
  }

  /**
   * Updates the room with the given data.
   *
   * @param roomData - Partial room data to add or remove.
   * @param add - When `true`, append to arrays; when `false`, remove the provided entries.
   * @returns A promise that resolves when the update request is sent.
   * @throws If there is no active room.
   */
  updateRoom(roomData, add: boolean) {
    if (!this.activeRoom) {
      throw new Error('no active room');
    }

    return this.chatSocket.sendRequest(
      this.activeRoom.id,
      ECHATMETHOD.UPDATE_ROOM,
      {
        roomData,
        add,
        from: this.myId,
        to: 'all'
      }
    );
  }

  /**
   * Re-announces the current room and last message to the server (e.g., after reconnect).
   *
   * @returns A promise resolving with the server response of the NEW_ROOM request.
   */
  reconnectToRoom() {
    if (!this.activeRoom) {
      return;
    }

    const room: IchatRoom<IpeerInfo> = {
      id: this.activeRoom.id,
      peers: this.activeRoom.peers,
      blocked: this.activeRoom.blocked,
      unviewedMsgsLength: this.activeRoom.unviewedMsgsLength,
      type: this.activeRoom.type,
      extras: this.activeRoom.extras,
      closed: this.activeRoom.closed,
      lastActive: this.activeRoom.lastActive,
      createTime: this.activeRoom.createTime
    };

    let lastMsg: ChatMsg | null = null;

    if (this.messages.length) {
      lastMsg = this.messages[this.messages.length - 1];
    } else {
      const found = this.allRooms.find(room => room.room.id === this.activeRoom?.id);

      if (found) {
        lastMsg = found.lastMsg;
      }
    }


    return this.chatSocket.sendOnlineSoloRequest(
      ECHATMETHOD.NEW_ROOM,
      {
        room,
        userId: this.myId,
        lastMsg
      }
    );
  }

  /**
   * Creates a new chat room session and joins it.
   *
   * @param room - The room instance and last message to announce.
   * @returns A promise that resolves after the room is created and joined.
   */
  async newRoom(room: IChatRoomAndLastMsg) {
    if (this.chatSocket.isSocketConnected()) {
      if (this.activeRoom) {
        if (this.activeRoom.id !== room.room.id) {
          this.clearRoom();
        } else {
          return;
        }
      }
    } else {
      this.chatSocket.connect();
    }
    this.activeRoom = room.room;

    const res = await this.chatSocket.sendOnlineSoloRequest(
      ECHATMETHOD.NEW_ROOM,
      {
        room: room.room,
        lastMsg: room.lastMsg,
        userId: this.myId
      }
    ) as { room: IchatRoom<IpeerInfo> };

    if (res && res.room) {
      const room = new ChatRoom(res.room);

      this.activeRoom = room;

      this.joinRoom();
    }
  }

  /**
   * Clears the active room and local message cache.
   */
  clearRoom() {
    this.activeRoom = null;
    this.messages.length = 0;
    this.messages = [];
  }

  /**
   * Scrolls to the last message in the chat.
   *
   * @param chatElementId - The ID of the chat container element.
   */
  scrollToLast(chatElementId: string) {
    if (document) {
      const elem = document?.getElementById(chatElementId);

      elem?.scrollIntoView();
    }
  }

  /**
   * Checks whether a given user id is currently online.
   *
   * @param id - The user id to check.
   * @returns The server response for the online status query.
   */
  async isOnline(id: string) {
    const res = await this.chatSocket.sendOnlineSoloRequest(
      ECHATMETHOD.IS_ONLINE,
      {
        userId: id
      }
    );

    return res;
  }

  /**
   * Destroys the client, signaling subscribers to teardown.
   */
  destroy() {
    this.destroyed$.next(true);
  }

  /**
   * Adds a new peer to the active room or updates its online state if already present.
   *
   * @param data - Partial peer information from server notification.
   */
  private newPeer(data: IpeerInfo) {
    this.logger.debug('newPeer, %o', data);

    const { id } = data;
    const peerInfo = this.getPeerInfo(id);

    if (peerInfo) {
      this.logger.warn('peer %s already existed!', id);
      peerInfo.online = data.online;

      return;
    }

    const peer = {
      id: data.id,
      name: data.name,
      photo: data.photo,
      roomAdmin: data.roomAdmin,
      online: data.online,
      lastSeen: new Date(),
      unviewedMsgsLength: data.unviewedMsgsLength
    };

    this.logger.debug('newPeer, %o', peer);
    if (this.activeRoom && this.activeRoom.peers) {
      this.activeRoom.peers = [...this.activeRoom.peers, peer];
    }
  }

  /**
   * Handles a peer closed event by marking the peer as offline.
   *
   * @param peerId - The id of the peer that closed.
   */
  private peerClosed(peerId: string) {
    const peerInfo = this.getPeerInfo(peerId);

    if (peerInfo) {
      peerInfo.online = false;
    }
  }

  /**
   * Finds peer information by id in the active room.
   *
   * @param peerId - The peer id to look up.
   * @returns The peer info if found; otherwise undefined.
   */
  private getPeerInfo(peerId: string) {
    return this.activeRoom && this.activeRoom.peers.find(peer => peer.id === peerId);
  }

  /**
   * Queries and updates local counters for unviewed messages for a peer and room.
   *
   * @param peerId - The peer id to compute counts for.
   * @param roomId - The target room id.
   */
  private async getUnviewedMsgsLength(peerId: string, roomId: string) {
    this.logger.debug('getUnviewedMsgsLength: %s', peerId);
    const res = await this.chatSocket.sendOnlineSoloRequest(
      ECHATMETHOD.GET_UNVIEWED_MSGS_LENGTH,
      {
        peerId,
        roomId
      }
    ) as { totalUnviewedLength: number; room: { roomId: string; unviewedMsgsLength: number; unviewedMsgsLengthByPeer: number } };

    this.logger.debug('getUnviewedMsgsLength, res: %s', JSON.stringify(res));

    if (res && typeof res.totalUnviewedLength === 'number') {
      this.unviewedMsgsLength = res.totalUnviewedLength;
    }

    if (res.room) {
      const foundIndex = this.allRooms.findIndex(room => room.room.id === res.room.roomId);

      if (foundIndex > -1) {
        if (typeof res.room.unviewedMsgsLength === 'number') {
          this.allRooms[foundIndex].room.unviewedMsgsLength = res.room.unviewedMsgsLength;
        }

        const peerInRoomIndex = this.allRooms[foundIndex].room.peers.findIndex(peer => peer.id === peerId);

        if (peerInRoomIndex > -1) {
          if (typeof res.room.unviewedMsgsLengthByPeer === 'number') {
            this.allRooms[foundIndex].room.peers[peerInRoomIndex].unviewedMsgsLength = res.room.unviewedMsgsLengthByPeer;
          }
        }
      }
    }

    this.logger.debug('getUnviewedMsgsLength, total: %o', this.unviewedMsgsLength);
    this.logger.debug('getUnviewedMsgsLength, rooms: %o', this.allRooms);

    // this.recalculateUnviewedMsgsLength();
  }

  /**
   * Subscribes to ChatSocket events and updates local state accordingly.
   *
   * @param chatElementId - The ID of the chat element to enable autoscroll on incoming messages.
   */
  private runSubscriptions(chatElementId: string) {
    this.chatSocket.eventbus.chat$
      .pipe(takeUntil((this.destroyed$)))
      .subscribe(event => {
        if (event && event.type === ECHATMETHOD.CHAT_MESSAGE) {
          const msg = event.data;

          const message = new ChatMsg(
            this.myId,
            {
              id: msg.id,
              peerInfo: msg.peerInfo,
              roomId: msg.roomId,
              msg: msg.msg,
              createTime: msg.createTime,
              who: 'partner',
              status: 'received',
              deleted: false
            }
          );

          if (message.roomId === this.activeRoom?.id) {
            this.messages = [...this.messages, message];
            this.scrollToLast(chatElementId);
            this.updateStatus('viewed', [message.id]);
          }

          if (message.peerInfo?.id) {
            this.logger.debug('getUnviewedMsgsLength: %s', message.peerInfo.id);
            this.getUnviewedMsgsLength(message.peerInfo.id, message.roomId);
          }
        }

        if (event && event.type === ECHATMETHOD.DELETE_MESSAGE) {
          const { id, deleted } = event.data;
          const found = this.messages
            .find(msg => msg.id === id);

          if (found) {
            found.deleted = deleted;
          }

          if (found && found.status !== 'viewed') {
            if (found.peerInfo?.id) {
              this.getUnviewedMsgsLength(found.peerInfo.id, found.roomId);
            }
          }
        }

        if (event && event.type === ECHATMETHOD.NEW_PEER) {
          this.newPeer(event.data);
        }

        if (event && event.type === ECHATMETHOD.NEW_MAIN_PEER) {
          this.logger.debug('new main peer: %s', JSON.stringify(event.data));
          if (this.activeRoom && event.data.id !== this.myId) {
            const foundIndex = this.activeRoom.peers.findIndex(peer => peer.id === event.data.id);

            if (foundIndex > -1) {
              this.activeRoom.peers[foundIndex].online = true;
            }
          }

          for (const room of this.allRooms) {
            const foundIndex = room.room.peers.findIndex(peer => peer.id === event.data.id);

            if (foundIndex > -1) {
              room.room.peers[foundIndex].online = true;
            }
          }
        }

        if (event && event.type === ECHATMETHOD.PEER_OFFLINE) {
          this.logger.debug('peer offline: %s', JSON.stringify(event.data));
          this.logger.debug('peer offline active room: %s', this.activeRoom);
          if (this.activeRoom) {
            const foundIndex = this.activeRoom.peers.findIndex(peer => peer.id === event.data.id);

            if (foundIndex > -1) {
              this.activeRoom.peers[foundIndex].online = false;
            }
          }

          this.logger.debug('peer offline all rooms: %s', JSON.stringify(this.allRooms));
          for (const room of this.allRooms) {
            this.logger.debug('peer offline event.data again: %s', JSON.stringify(event.data));
            const foundIndex = room.room.peers.findIndex(peer => peer.id === event.data.id);

            this.logger.debug('peer offline foundIndex: %s', foundIndex);

            if (foundIndex > -1) {
              room.room.peers[foundIndex].online = false;
            }
          }

          this.logger.debug('peer offline all rooms after: %s', JSON.stringify(this.allRooms));
        }

        if (event && event.type === ECHATMETHOD.PEER_ONLINE) {
          this.logger.debug('peer online: %s', JSON.stringify(event.data));
          this.logger.debug('peer online active room: %s', this.activeRoom);
          if (this.activeRoom) {
            const foundIndex = this.activeRoom.peers.findIndex(peer => peer.id === event.data.id);

            if (foundIndex > -1) {
              this.activeRoom.peers[foundIndex].online = true;
            }
          }

          this.logger.debug('peer online all rooms: %s', JSON.stringify(this.allRooms));
          for (const room of this.allRooms) {
            this.logger.debug('peer online event.data again: %s', JSON.stringify(event.data));
            const foundIndex = room.room.peers.findIndex(peer => peer.id === event.data.id);

            this.logger.debug('peer online foundIndex: %s', foundIndex);

            if (foundIndex > -1) {
              room.room.peers[foundIndex].online = true;
            }
          }

          this.logger.debug('peer online all rooms after: %s', JSON.stringify(this.allRooms));
        }

        if (event && event.type === ECHATMETHOD.PEER_CLOSE) {
          this.peerClosed(event.data.peerId);
        }

        if (event && event.type === ECHATMETHOD.MAIN_PEER_CLOSE) {
          // this.manageMainPeerLeave(event.data);
        }

        if (event && event.type === ECHATMETHOD.ROOM_CREATED) {
          this.logger.debug('room created: %s', JSON.stringify(event.data));

          const { roomAndLastMsg } = event.data;

          const found = this.allRooms.find(room => room.room.id === roomAndLastMsg.room.id);

          if (!found) {
            const room = new ChatRoom(roomAndLastMsg.room);

            const lastMsg = roomAndLastMsg.lastMsg ? new ChatMsg(roomAndLastMsg.lastMsg.from, roomAndLastMsg.lastMsg) : null;


            this.allRooms = [...this.allRooms, { room, lastMsg }];

            this.logger.debug('room created all rooms: %s', JSON.stringify(this.allRooms));
          }

          /* if (event.data && event.data.from === this.myId) {
              this.joinRoom();
            } */
        }

        if (event && event.type === ECHATMETHOD.SOCKET_CONNECTED) {
          this.joinMainRoom();
        }

        if (event && event.type === ECHATMETHOD.UPDATE_STATUS) {
          const { id, status } = event.data;
          const msg = this.messages
            .find(val => val.id === id);

          if (msg) {
            msg.status = status;

            if (msg.peerInfo?.id) {
              this.getUnviewedMsgsLength(msg.peerInfo.id, msg.roomId);
            }
          }
        }

        if (event && event.type === ECHATMETHOD.PEER_UPDATE) {
          const { peerInfo } = event.data;
          const localPeerInfo = this.activeRoom?.getPeerById(peerInfo.id);

          if (localPeerInfo) {
            localPeerInfo.roomAdmin = peerInfo.roomAdmin;
          }
        }

        if (event && event.type === ECHATMETHOD.UPDATE_ROOM) {
          const { roomData, add } = event.data;

          if (this.activeRoom && this.activeRoom.id === roomData.id) {
            // this is handleded else where
            if (roomData.unviewedMsgsLength) {
              delete roomData.unviewedMsgsLength;
            }

            this.activeRoom?.update(roomData, add);
          }

          if (roomData.unviewedMsgsLength && this.activeRoom && this.activeRoom.id === roomData.id) {
            this.getUnviewedMsgsLength(this.myId, this.activeRoom.id);
          }

          // this.recalculateUnviewedMsgsLength();
        }

        if (event && event.type === ECHATMETHOD.UPDATE_VIEW_COUNT) {
          this.logger.debug('update view count: %s', JSON.stringify(event.data));
          const { peerId, roomId } = event.data;

          this.getUnviewedMsgsLength(peerId, roomId);
        }
      });
  }
}
